import api, { setAuthCallbacks } from '@/shared/config/api.js';
import { setCookie, getCookie, removeCookie } from '@/shared/utils/cookies';

// Constants
const TOKEN_KEYS = {
  ACCESS: 'access_token',
  REFRESH: 'refresh_token',
  EXPIRY: 'token_expiry',
};

const REFRESH_MARGIN_MS = 30000; // 30 seconds before expiry

// State
let refreshTimeout = null;
let isRefreshing = false;
let refreshPromise = null;

// ===========================
// Token Management
// ===========================
function saveTokens(data) {
  if (!data?.access_token || !data?.refresh_token) {
    throw new Error('Invalid token data');
  }

  // Use expires_in from API response (in seconds)
  const expiresIn = data.expires_in || 7200; // Default 2 hours if not provided
  const refreshExpiresIn = data.refresh_expires_in || 864000; // Default 10 days if not provided

  // Calculate expiry time in milliseconds
  const expiryTime = Date.now() + expiresIn * 1000;

  // Convert seconds to days for cookie expiry
  // Use at least 1 day for cookies, but store actual expiry in token_expiry cookie
  const accessExpireDays = Math.max(Math.ceil(expiresIn / 86400), 1);
  const refreshExpireDays = Math.ceil(refreshExpiresIn / 86400);

  setCookie(TOKEN_KEYS.ACCESS, data.access_token, accessExpireDays);
  setCookie(TOKEN_KEYS.REFRESH, data.refresh_token, refreshExpireDays);
  setCookie(TOKEN_KEYS.EXPIRY, expiryTime.toString(), accessExpireDays);

  // Schedule auto-refresh 30 seconds before expiry
  scheduleTokenRefresh(expiresIn);
}

export function getToken() {
  return getCookie(TOKEN_KEYS.ACCESS);
}

export function getRefreshToken() {
  return getCookie(TOKEN_KEYS.REFRESH);
}

export function isTokenExpired() {
  const expiry = getCookie(TOKEN_KEYS.EXPIRY);
  if (!expiry) return true;
  return Date.now() > parseInt(expiry, 10) - REFRESH_MARGIN_MS;
}

export function clearTokens() {
  removeCookie(TOKEN_KEYS.ACCESS);
  removeCookie(TOKEN_KEYS.REFRESH);
  removeCookie(TOKEN_KEYS.EXPIRY);
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
  isRefreshing = false;
  refreshPromise = null;
}

// ===========================
// Auto Refresh
// ===========================
function scheduleTokenRefresh(expiresIn) {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }

  // Refresh 30 seconds before expiry, but ensure minimum 5 seconds delay
  const refreshTime = Math.max((expiresIn - 30) * 1000, 5000);

  refreshTimeout = setTimeout(async () => {
    try {
      await refreshToken();
    } catch (err) {
      console.error('Auto-refresh failed:', err);
      clearTokens();
    }
  }, refreshTime);
}

// ===========================
// API Methods
// ===========================
export async function login(phone_number, password) {
  if (!phone_number || !password) {
    throw new Error('شماره تلفن و رمز عبور الزامی است');
  }

  try {
    const { data } = await api.post('/tenant/v1/auth/login', {
      phone_number,
      password,
    });

    if (data?.success && data.data) {
      // Check if user is pending verification
      if (data.data.is_pending === true) {
        // User needs OTP verification
        // Return special error with phone number for redirect
        const pendingError = new Error(
          'لطفا ابتدا حساب کاربری خود را تایید کنید',
        );
        pendingError.isPending = true;
        pendingError.phone_number = data.data.phone_number || phone_number;
        throw pendingError;
      }

      // User is verified, save tokens
      saveTokens(data.data);
      return data.data;
    }

    // Check if response indicates pending verification (even if success is false)
    if (data?.data?.is_pending === true) {
      const pendingError = new Error(
        data.message || 'لطفا ابتدا حساب کاربری خود را تایید کنید',
      );
      pendingError.isPending = true;
      pendingError.phone_number = data.data.phone_number || phone_number;
      throw pendingError;
    }

    throw new Error('خطای ورود به سیستم');
  } catch (err) {
    // Check if it's our pending error
    if (err.isPending) {
      throw err;
    }

    // Check if error has is_pending flag (from interceptor processed error)
    if (err.data?.is_pending === true) {
      const pendingError = new Error(
        err.message || 'لطفا ابتدا حساب کاربری خود را تایید کنید',
      );
      pendingError.isPending = true;
      pendingError.phone_number = err.data.phone_number || phone_number;
      throw pendingError;
    }

    // Check if response has is_pending flag (401 error case - before interceptor)
    if (err.response?.data?.data?.is_pending === true) {
      const pendingError = new Error(
        err.response.data.message || 'لطفا ابتدا حساب کاربری خود را تایید کنید',
      );
      pendingError.isPending = true;
      pendingError.phone_number =
        err.response.data.data.phone_number || phone_number;
      throw pendingError;
    }

    // Preserve the full error object from API
    // Axios interceptor already processed the error, so it's directly in err
    if (err && typeof err === 'object' && err.errors) {
      throw err;
    }
    if (err.response?.data) {
      throw err.response.data;
    }
    throw err.message ? new Error(err.message) : new Error('خطای سرور');
  }
}

export async function signup(phone_number, password, password_confirm) {
  if (!phone_number || !password || !password_confirm) {
    throw new Error('تمام فیلدها الزامی است');
  }

  if (password !== password_confirm) {
    throw new Error('رمز عبور و تکرار آن یکسان نیستند');
  }

  try {
    const { data } = await api.post('/tenant/v1/auth/signup', {
      phone_number,
      password,
      password_confirm,
    });

    // Signup no longer returns tokens, just success message
    if (data?.success) {
      return data;
    }
    throw new Error('خطای ثبت نام');
  } catch (err) {
    // Preserve the full error object from API
    // Axios interceptor already processed the error, so it's directly in err
    if (err && typeof err === 'object' && err.errors) {
      throw err;
    }
    if (err.response?.data) {
      throw err.response.data;
    }
    throw err.message ? new Error(err.message) : new Error('خطای سرور');
  }
}

export async function refreshToken() {
  // Prevent multiple simultaneous refresh requests
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refresh = getRefreshToken();
  if (!refresh) {
    throw new Error('No refresh token available');
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      // Don't show toast for refresh token requests
      const { data } = await api.post('/tenant/v1/auth/refresh-token', null, {
        showToast: false,
      });

      // Refresh token response is directly in data, not data.data
      if (data?.access_token && data?.refresh_token) {
        saveTokens(data);
        return data;
      }
      throw new Error('Refresh failed - invalid response structure');
    } catch (err) {
      console.error('Refresh token error:', err);
      clearTokens();
      throw err;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function logout() {
  try {
    // No logout endpoint in API, just clear tokens
    clearTokens();
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('Logout request failed:', err);
    }
    clearTokens();
  }
}

export async function sendOTP(phone_number) {
  if (!phone_number) {
    throw new Error('شماره تلفن الزامی است');
  }

  try {
    const { data } = await api.post('/tenant/v1/auth/otp-send', {
      phone_number,
    });

    return data;
  } catch (err) {
    // Preserve the full error object from API
    // Axios interceptor already processed the error, so it's directly in err
    if (err && typeof err === 'object' && err.errors) {
      throw err;
    }
    if (err.response?.data) {
      throw err.response.data;
    }
    throw err.message ? new Error(err.message) : new Error('خطای ارسال کد');
  }
}

export async function resetPassword(
  phone_number,
  otp,
  password,
  password_confirm,
) {
  if (!phone_number || !otp || !password || !password_confirm) {
    throw new Error('تمام فیلدها الزامی است');
  }

  if (password !== password_confirm) {
    throw new Error('رمز عبور و تکرار آن یکسان نیستند');
  }

  try {
    const { data } = await api.post('/tenant/v1/auth/reset-password', {
      phone_number,
      otp,
      password,
      password_confirm,
    });

    return data;
  } catch (err) {
    // Preserve the full error object from API
    // Axios interceptor already processed the error, so it's directly in err
    if (err && typeof err === 'object' && err.errors) {
      throw err;
    }
    if (err.response?.data) {
      throw err.response.data;
    }
    throw err.message
      ? new Error(err.message)
      : new Error('خطای بازیابی رمز عبور');
  }
}

export async function verifyOTP(phone_number, otp) {
  if (!phone_number || !otp) {
    throw new Error('شماره تلفن و کد تایید الزامی است');
  }

  try {
    const { data } = await api.post('/tenant/v1/auth/otp-verify', {
      phone_number,
      otp,
    });

    if (data?.success && data.data) {
      saveTokens(data.data);
      return data.data;
    }
    throw new Error('خطای تایید کد');
  } catch (err) {
    // Preserve the full error object from API
    // Axios interceptor already processed the error, so it's directly in err
    if (err && typeof err === 'object' && err.errors) {
      throw err;
    }
    if (err.response?.data) {
      throw err.response.data;
    }
    throw err.message ? new Error(err.message) : new Error('خطای سرور');
  }
}

export async function changePassword(
  old_password,
  new_password,
  new_password_confirm,
) {
  if (!old_password || !new_password || !new_password_confirm) {
    throw new Error('تمام فیلدها الزامی است');
  }

  if (new_password !== new_password_confirm) {
    throw new Error('رمز عبور جدید و تکرار آن یکسان نیستند');
  }

  try {
    const { data } = await api.put('/tenant/v1/auth/change-password', {
      old_password,
      new_password,
      new_password_confirm,
    });

    return data;
  } catch (err) {
    // Preserve the full error object from API
    // Axios interceptor already processed the error, so it's directly in err
    if (err && typeof err === 'object' && err.errors) {
      throw err;
    }
    if (err.response?.data) {
      throw err.response.data;
    }
    throw err.message
      ? new Error(err.message)
      : new Error('خطای تغییر رمز عبور');
  }
}

export function isAuthenticated() {
  const token = getToken();
  const refreshToken = getRefreshToken();
  return !!(token || refreshToken);
}

// ===========================
// Token Parsing
// ===========================
export function getUserFromToken() {
  const token = getToken();
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid token format');
      return null;
    }

    // Decode base64url (JWT uses base64url encoding, not standard base64)
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));

    return {
      id: payload.sub,
      username: payload.preferred_username,
      email: payload.email,
      name: payload.name,
      given_name: payload.given_name,
      family_name: payload.family_name,
      roles: payload.resource_access || {},
    };
  } catch (err) {
    console.error('Failed to parse token:', err);
    clearTokens(); // Clear invalid token
    return null;
  }
}

// ===========================
// Initialize
// ===========================
// Register auth callbacks with API instance
setAuthCallbacks({
  getToken,
  refreshToken,
  clearTokens,
});

// Export default object
export default {
  login,
  signup,
  logout,
  refreshToken,
  sendOTP,
  verifyOTP,
  resetPassword,
  changePassword,
  getToken,
  getRefreshToken,
  isAuthenticated,
  isTokenExpired,
  clearTokens,
  getUserFromToken,
};
